/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/core";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import React from "react";
import LazyLoad from "react-lazyload";
import { CloseButton } from "ui/chakra/components/CloseButton";
import { ChakraUIThemeProviderForMigration } from "ui/chakraUiMigration/theme/ChakraUIThemeProviderForMigration";

import { MEDIA_QUERY_FOR_SP } from "../../constants/mediaQueryForSp";
import { EventGoodsStatusBadges } from "../eventGoods/EventGoodsStatusBadges";
import { AddToCartButton } from "./AddToCartButton";

import type { WatchlistItem } from "./types/WatchlistItem";

type Props = {
  item: WatchlistItem;
  onClickRemove: React.MouseEventHandler<HTMLButtonElement>;
  onClickCard: React.MouseEventHandler<HTMLDivElement>;
  onClickAddToCart: React.MouseEventHandler<HTMLButtonElement>;
};

export const AccountWatchlistCard: React.FC<Props> = ({
  item,
  onClickRemove,
  onClickCard,
  onClickAddToCart,
}) => {
  const classes = cardStyles();

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // カードと連動しない要素（data-no-card-click）がクリックされた場合は無視する
    // HTMLElement と SVGElement の両方（ボタン内の SVG パス等）に対応
    if (
      (e.target instanceof HTMLElement || e.target instanceof SVGElement) &&
      e.target.closest("[data-no-card-click]")
    ) {
      return;
    }
    onClickCard(e);
  };

  return (
    <Card css={classes.goodsItem} variant="outlined" onClick={handleCardClick}>
      <div css={classes.removeButtonContainer}>
        <CloseButton
          onClick={onClickRemove}
          title="ウォッチリストから削除"
          data-no-card-click
        />
      </div>
      <div css={classes.imageContainer}>
        <LazyLoad height={"100%"} offset={400} once>
          <img
            className="goodsImage"
            src={item.thumbnail}
            alt={item.className}
          />
        </LazyLoad>
      </div>
      <div css={classes.goodsItemInfo}>
        <h3>
          {item.className}
          {item.hasVariants && item.subClassName
            ? `（${item.subClassName}）`
            : ""}
        </h3>
        <p css={classes.goodsItemPrice}>
          {item.hasVariants ? "各 ¥" : "¥"}
          {item.price.toLocaleString()}
          {EventGoodsStatusBadges[item.eventGoodsStatus]}
        </p>
      </div>
      <Divider variant="middle" />
      <div css={classes.addToCartButtonContainer} data-no-card-click>
        {item.eventGoodsStatus === "ON_SALE" && (
          <ChakraUIThemeProviderForMigration>
            <AddToCartButton onClick={onClickAddToCart} />
          </ChakraUIThemeProviderForMigration>
        )}
      </div>
    </Card>
  );
};

const cardStyles = () => {
  return {
    goodsItem: css`
      position: relative;
      cursor: pointer;
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 0px 5px rgba(0, 0, 0, 0.15);
      }
      h3 {
        font-size: 1rem;
        font-weight: bold;
        line-height: 1.5em;
        overflow: hidden;
      }
      margin-left: 4px;
      margin-right: 4px;
    `,
    imageContainer: css`
      position: relative;
      &::before {
        display: block;
        content: "";
        width: 100%;
        padding-top: 100%;
        background-color: #e3e3e3;
      }
      img {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    `,
    removeButtonContainer: css`
      padding: 4px 4px 4px 4px;
      display: flex;
      justify-content: flex-end;
    `,
    goodsItemInfo: css`
      padding: 15px;
      ${MEDIA_QUERY_FOR_SP} {
        padding: 14px;
      }
    `,
    goodsItemPrice: css`
      font-size: 0.875rem;
      color: #000;
      margin-bottom: 5px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `,
    addToCartButtonContainer: css`
      text-align: right;
      padding: 16px;
      height: 64px;
    `,
  };
};
